import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useDispatch } from "react-redux";
import { getUserSurveyOptions, getUserSurveyChoices, setUserSurveyChoices, skipUserPreferenceSurvey } from "redux/actions";
import "../style/aivrComponents.scss";
import { Select, Button, Modal, notification } from "antd";
import _ from "lodash";
import { Input } from "antd";

const REGION_ROUTE_MAP = {
    Eastern: ["Anglia", "East Midlands", "North East", "East Coast"],
    "North West & Central": ["North West", "Central", "West Coast"],
    "Scotland's Railway": ["Scotland"],
    Southern: ["Kent", "Sussex", "Wessex", "Network Rail High Speed"],
    "Wales & Western": ["Wales", "Western"],
};

const NewUserModal = ({ onClose }) => {
    const [modalOptions, setModalOptions] = useState({});
    const [userPreferences, setUserPreferences] = useState([]);

    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(getUserSurveyOptions()).then((resp) => setModalOptions(resp));
        dispatch(getUserSurveyChoices()).then((resp) => {
            setUserPreferences(resp);
        });
    }, [dispatch]);

    const { Option } = Select;
    const { TextArea } = Input;

    const skipModal = () => {
        dispatch(skipUserPreferenceSurvey());
        onClose();
    };

    const handleTagSelection = useCallback(
        (category, option) => {
            const selected = _.some(userPreferences, { category, option });
            setUserPreferences((currentPreferences) => {
                if (selected) {
                    return _.reject(currentPreferences, { category, option });
                } else {
                    return [...currentPreferences, { category, option, description: null }];
                }
            });
        },
        [userPreferences],
    );

    const routeOptions = useMemo(() => {
        return _.reduce(
            userPreferences,
            (acc, cur) => {
                if (cur?.category === "Region" && REGION_ROUTE_MAP[cur?.option]) {
                    acc.push(REGION_ROUTE_MAP[cur?.option]);
                }
                return _.flatMap(acc);
            },
            [],
        );
    }, [userPreferences]);

    const handleRoutesAndRegionsChange = useCallback((regions, category) => {
        setUserPreferences((currentPreferences) =>
            _.filter(currentPreferences, (item) => item.category !== category).concat(
                _.map(regions, (region) => ({ category: category, option: region, description: null })),
            ),
        );
    }, []);

    const submitUserSelection = () => {
        const formatUserPreferences = _.map(userPreferences, (preference) => ({ ...preference, categoryId: modalOptions[preference.category].id }));
        dispatch(setUserSurveyChoices(formatUserPreferences)).then((resp) => {
            if (resp?.success) {
                notification.success({
                    message: "Thank you",
                    description: "We've saved your choices",
                });
            }
        });
        onClose();
    };

    const highlightSelectedTags = useCallback((category, option) => _.some(userPreferences, { category, option }), [userPreferences]);

    const handleOtherInput = useCallback((category, description) => {
        setUserPreferences((currentPreferences) =>
            _.map(currentPreferences, (p) => {
                if (p?.category === category && p?.option === "Other") return { ...p, description };
                return p;
            }),
        );
    }, []);

    const renderCategory = useCallback(
        (category) => {
            if (!_.has(modalOptions, category)) {
                return null;
            }

            const descriptionValue = _.get(_.find(userPreferences, { category, option: "Other" }), "description", null);

            return (
                <div className="new-user-modal__container">
                    <h3 className="new-user-modal__divider">{category}(s)</h3>
                    <ul className="new-user-modal__container--tag">
                        {_.get(modalOptions, [category, "options"], []).map((option, idx) => {
                            return (
                                <li
                                    key={idx}
                                    className={`new-user-modal__tag ${highlightSelectedTags(category, option) ? "new-user-modal__tag--highlight" : ""}`}
                                    onClick={() => handleTagSelection(category, option)}>
                                    {option}
                                </li>
                            );
                        })}
                    </ul>
                    {_.some(userPreferences, { category, option: "Other" }) && (
                        <TextArea
                            value={descriptionValue}
                            className="new-user-modal__textarea"
                            rows={2}
                            placeholder={`Tell us more about your ${category.toLowerCase()}`}
                            onChange={(e) => handleOtherInput(category, e.target.value)}
                        />
                    )}
                </div>
            );
        },
        [handleTagSelection, highlightSelectedTags, modalOptions, userPreferences, handleOtherInput],
    );

    const regionAndRoutes = useMemo(() => {
        if (!modalOptions || _.isEmpty(modalOptions)) {
            return null;
        }
        const regionSelection = _.filter(userPreferences, (option) => option?.category === "Region" && option?.option);
        const content = [
            <Select
                mode="multiple"
                className="new-user-modal__select--top"
                placeholder="Select your regions"
                maxTagCount={4}
                onChange={(e) => handleRoutesAndRegionsChange(e, "Region")}
                value={_.map(regionSelection, (item) => item?.option)}
                allowClear
                notFoundContent={<p style={{ fontSize: "12px" }}>We didn't find anything matching your search...</p>}
                showArrow>
                {_.get(modalOptions, ["Region", "options"], []).map((item, index) => (
                    <Option
                        value={item}
                        key={index}
                        style={{ color: "#2c2f45" }}>
                        {item}
                    </Option>
                ))}
            </Select>,
        ];

        if (modalOptions?.Route) {
            const routeSelection = _.filter(userPreferences, (option) => option?.category === "Route");
            content.push(
                <Select
                    mode="multiple"
                    className="new-user-modal__select--bottom"
                    placeholder="Select your routes"
                    disabled={!regionSelection || !regionSelection.length}
                    maxTagCount={4}
                    onChange={(e) => handleRoutesAndRegionsChange(e, "Route")}
                    value={_.map(routeSelection, (item) => item?.option)}
                    allowClear
                    notFoundContent={<p style={{ fontSize: "12px" }}>We didn't find anything matching your search...</p>}
                    showArrow>
                    {_.map(routeOptions.sort(), (item, index) => {
                        return (
                            <Option
                                value={item}
                                key={index}
                                style={{ color: "#2c2f45" }}>
                                {item}
                            </Option>
                        );
                    })}
                </Select>,
            );
        }

        return content;
    }, [handleRoutesAndRegionsChange, modalOptions, userPreferences, routeOptions]);

    return (
        <Modal
            className="new-user-modal"
            title="User Preferences"
            visible={true}
            onCancel={skipModal}
            width={750}
            footer={[
                <div className="new-user-modal__footer">
                    <p className="new-user-modal__text">We'll use this information to customise the data you see.</p>
                    <div>
                        <Button
                            key="skip"
                            className="new-user-modal__btn"
                            onClick={skipModal}>
                            Skip
                        </Button>
                        <Button
                            key="submit"
                            className="new-user-modal__btn"
                            disabled={userPreferences.length === 0}
                            type="primary"
                            onClick={submitUserSelection}>
                            Submit
                        </Button>
                    </div>
                </div>,
            ]}>
            {_.has(modalOptions, "Region") && (
                <div className="new-user-modal__container new-user-modal__container--border">
                    <h3 className="new-user-modal__divider">{`Regions${_.has(modalOptions, "Route") ? " and Routes" : ""}`}</h3>
                    {regionAndRoutes}
                </div>
            )}
            {renderCategory("Discipline")}
            {renderCategory("Use Case")}
        </Modal>
    );
};

export default NewUserModal;
